import { GroupBase } from "react-select";
import { IPipelineStageSelectOption } from "@components/AddNewEntity";
import { DealStage } from "@app-types/common.type";
import { AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT } from "@config/constants";
import { DealStageStatus } from "@enums/common.enum";
import { ICreateDealForm } from "@components/AddNewEntity/Deal/AddNewDealDialog";
import { CreateDealRequest } from "@app-types/requests.type";
import { addUrlProtocol, getBaseUrl } from "@utils/strings.util";
import { CompanyTypeSubtype } from "@enums/companies.enum";
import { IUpdateCompanyData } from "@interfaces/company.interface";
import { IAttribute } from "@interfaces/attribute.interface";
import { filterAndProcessAttributeValues } from "@components/Attributes";

export const stagesToGroupedDropdownOptions = (
  stages: DealStage[],
  hasCompany: boolean = false
): GroupBase<IPipelineStageSelectOption>[] => {
  let dealStagesOptions = stages.map(stageToDropdownOption);
  let portfolioStagesOptions = dealStagesOptions.filter(
    (stage) => stage.status === DealStageStatus.PORTFOLIO
  );
  portfolioStagesOptions = hasCompany
    ? portfolioStagesOptions
    : portfolioStagesOptions.map((i) => ({ ...i, disabled: true }));

  dealStagesOptions = dealStagesOptions.filter(
    (stage) => stage.status !== DealStageStatus.PORTFOLIO
  );

  return [
    {
      label: AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT.DEAL_PIPELINE,
      options: dealStagesOptions,
    },
    ...(portfolioStagesOptions.length > 0
      ? [
          {
            label: AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT.PORTFOLIO_PIPELINE,
            options: portfolioStagesOptions,
          },
        ]
      : []),
  ] as GroupBase<IPipelineStageSelectOption>[];
};

export const stageToDropdownOption = (stage: DealStage): IPipelineStageSelectOption => ({
  color: stage?.color || "",
  value: stage?.id || "",
  label: stage?.title || "",
  status: stage?.status,
});

export type CompanyDataResult = Pick<
  CreateDealRequest,
  "company" | "external_company" | "update_company_data"
>;

export const getCompanyData = (
  data: ICreateDealForm,
  companyAttributes: IAttribute[] = []
): CompanyDataResult | null => {
  const company = data.company;

  if (!company || !company.website) {
    return {
      company: null,
      update_company_data: undefined,
      external_company: undefined,
    };
  }

  const companyData: IUpdateCompanyData = {
    title: company.label,
    website: getBaseUrl(addUrlProtocol(company.website)),
    priority: company.priority?.id,
    type: company.type?.id,
    description: company.description,
    stage_object_id: data.stage?.value || null,
    content_type: "deals",
    ...(company.type?.subtype === CompanyTypeSubtype.ADD_ON && {
      platform_company: company.platform_company,
    }),
    users: data.users?.map((i) => i.id) || null,
  };

  // The company doesn't exist yet, so its attribute values ride along with the deal — an existing
  // one is PATCHed separately before the deal is created.
  const newCompanyAttributeValues = filterAndProcessAttributeValues(
    data.attribute_values ?? [],
    companyAttributes
  );

  // ## This is a manual company that is created when the user types in a company name that is not in the list
  if (company.is_manual) {
    return {
      update_company_data: { ...companyData, attribute_values: newCompanyAttributeValues },
    };
  }

  // ## This is a company that is already in the list (GlobalSearch)
  if (company.suggestion) {
    return {
      update_company_data: { ...companyData, attribute_values: newCompanyAttributeValues },
      external_company: company.suggestion,
    };
  }

  return {
    company: company.value,
    update_company_data: companyData,
  };
};
